import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Loader2, Calculator } from "lucide-react";
import { ModuleCard } from "../components/ModuleCard";
import { CostEstimatePanel } from "../components/CostEstimatePanel";

export function ModuleCostsTab() {
    const { t, i18n } = useTranslation();
    const language = i18n.language?.startsWith("es") ? "es" : "en";
    const [catalog, setCatalog] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [estimate, setEstimate] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const { call: getCatalog } = useBluelagoon({ method: "getModuleCostEstimates" });
    const { call: getEstimate } = useBluelagoon({ method: "getModuleCostEstimates" });

    const fetchCatalog = useCallback(async () => {
        setIsLoading(true);
        const res = await getCatalog({});
        if (res?.data?.catalog) setCatalog(res.data.catalog);
        setIsLoading(false);
    }, [getCatalog]);

    useEffect(() => { fetchCatalog(); }, [fetchCatalog]);

    const fetchEstimate = useCallback(async (modules) => {
        if (modules.size === 0) { setEstimate(null); return; }
        const res = await getEstimate({ modules: [...modules].join(",") });
        if (res?.data?.estimate) setEstimate(res.data);
    }, [getEstimate]);

    const toggleModule = useCallback((key) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            fetchEstimate(next);
            return next;
        });
    }, [fetchEstimate]);

    const primaryModules = useMemo(() =>
        catalog.filter((m) => m.frequency >= 1), [catalog]);
    const secondaryModules = useMemo(() =>
        catalog.filter((m) => m.frequency > 0 && m.frequency < 1), [catalog]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <Calculator className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                    <h2 className="text-2xl font-bold">{t("billing.moduleCosts.title") || "AI Capacity Estimator"}</h2>
                    <p className="text-sm text-gray-500 mt-1">{t("billing.moduleCosts.subtitle") || "Select the modules your app needs to get a build cost estimate"}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Section title={t("billing.moduleCosts.coreModules") || "Core Modules"}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {primaryModules.map((mod) => (
                                <ModuleCard key={mod.key} mod={mod} isSelected={selected.has(mod.key)} onToggle={toggleModule} t={t} language={language} />
                            ))}
                        </div>
                    </Section>
                    <Section title={t("billing.moduleCosts.additionalModules") || "Additional Modules"}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                            {secondaryModules.map((mod) => (
                                <ModuleCard key={mod.key} mod={mod} isSelected={selected.has(mod.key)} onToggle={toggleModule} t={t} language={language} />
                            ))}
                        </div>
                    </Section>
                </div>
                <div className="lg:col-span-1">
                    {selected.size > 0 ? (
                        <CostEstimatePanel estimate={estimate} t={t} />
                    ) : (
                        <div className="rounded-2xl border-2 border-dashed border-gray-200 p-8 text-center">
                            <Calculator className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                            <p className="text-sm text-gray-400">{t("billing.moduleCosts.selectPrompt") || "Select modules to see cost estimate"}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">{title}</h3>
            {children}
        </div>
    );
}
